import { ExtractPropTypes, PropType } from 'vue'

function ajax() {}

/**
 * 选择框模式类型
 */
export type SelectMode = 'default' | 'fill' | 'none' | 'plain' | 'line' | string

/**
 * 选择值的类型
 */
export type SelectValueType = string | number | (string | number)[]

/**
 * 选择框选项数据类型
 */
export type SelectOptionsData = {
	/**
	 * 默认的选项标题字段
	 */
	label?: string | unknown
	/**
	 * 默认的选项值字段
	 */
	value?: string | number | unknown
	disabled?: boolean
	hide?: boolean
	[props: string]: any
}

/**
 * 箭头类型
 */
export type ArrowType = 'browse' | 'next' | ''

/**
 * 选项显示模式
 */
export type OptionDisplay = 'none' | 'float' | 'popup' | string

export const selectProps = {
	flex: Boolean,
	/**
	 * 懒加载
	 */
	lazy: {
		type: Boolean,
		default: true
	},
	/**
	 * 原生模式（性能极端低下情况下使用）
	 */
	native: {
		type: Boolean,
		default: false
	},
	/**
	 * 表单校验描述
	 */
	desc: {
		type: String,
		default: ''
	},
	/**
	 * 表单校验规则
	 */
	rule: {
		type: String,
		default: ''
	},
	/**
	 * 元素ID
	 */
	id: {
		type: String,
		default: ''
	},
	/**
	 * 是否必填
	 */
	required: {
		type: Boolean,
		default: false
	},
	/**
	 * 安全字符过滤
	 */
	spChars: {
		type: [String, Boolean],
		default: '/,\\,[,],{,},"' // 微软拼音输入法使用单引号做上屏前多个字符分隔符，不能过滤单引号
	},
	/**
	 * 单位
	 */
	unit: {
		type: String,
		default: ''
	},
	/**
	 * 分割符号
	 */
	splitSymbol: {
		type: String,
		default: ''
	},
	/**
	 * Tab索引
	 */
	tabindex: {
		type: String,
		default: '1'
	},
	/**
	 * 值字段名
	 */
	valueText: {
		type: String,
		default: 'value'
	},
	/**
	 * 标签字段名
	 */
	labelText: {
		type: String,
		default: 'label'
	},
	/**
	 * 绑定值
	 */
	modelValue: {
		type: [String, Number, Array] as PropType<SelectValueType>,
		default: ''
	},
	/**
	 * 选择框数据
	 */
	data: {
		type: [Array, String] as PropType<SelectOptionsData[] | string>,
		default: () => []
	},
	/**
	 * 占位符文本
	 */
	placeholder: {
		type: String,
		default: ''
	},
	/**
	 * 占位符颜色
	 */
	placeholderColor: {
		type: String,
		default: ''
	},
	/**
	 * 无匹配数据文本
	 */
	nomatchText: {
		type: String,
		default: ''
	},
	/**
	 * 暂无数据文本
	 */
	nodataText: {
		type: String,
		default: ''
	},
	disabled: {
		type: Boolean,
		default: false
	},
	/**
	 * 是否多选
	 */
	multiple: {
		type: Boolean,
		default: false
	},
	/**
	 * 是否可过滤
	 */
	filterable: {
		type: Boolean,
		default: false
	},
	/**
	 * 是否可输入
	 */
	inputable: {
		type: Boolean,
		default: false
	},
	/**
	 * 使用全选文本
	 */
	useAllText: {
		type: Boolean,
		default: false
	},
	/**
	 * 全选文本
	 */
	allText: {
		type: String,
		default: ''
	},
	/**
	 * 是否可清空
	 */
	clearable: {
		type: Boolean,
		default: true
	},
	/**
	 * 多选是否可逐个删除
	 */
	removable: {
		type: Boolean,
		default: true
	},
	/**
	 * 选择框颜色
	 */
	color: {
		type: String,
		default: 'default'
	},
	/**
	 * 选择框模式
	 */
	mode: {
		type: String as PropType<SelectMode>,
		default: 'default'
	},
	/**
	 * 选择框宽度
	 */
	width: {
		type: [Number, String],
		default: ''
	},
	/**
	 * 箭头类型
	 */
	arrowType: {
		type: String as PropType<ArrowType>,
		default: ''
	},
	/**
	 * 选项宽度
	 */
	optionWidth: {
		type: [Number, String],
		default: ''
	},
	/**
	 * 选项显示模式
	 */
	optionDisplay: {
		type: [Number, String] as PropType<OptionDisplay>,
		default: ''
	},
	/**
	 * 选项列数
	 */
	optionCol: {
		type: [Number, String],
		default: 1
	},
	/**
	 * 选项换行
	 */
	optionWrap: {
		type: Boolean,
		default: false
	},
	/**
	 * 选项对齐方式
	 */
	optionAlign: {
		type: String as PropType<'left' | 'right' | 'center'>,
		default: 'left'
	},
	/**
	 * HTTP请求函数
	 */
	httpRequest: {
		type: Function,
		default: ajax
	},
	/**
	 * API地址
	 */
	api: {
		type: String,
		default: ''
	},
	/**
	 * API参数
	 */
	apiParam: {
		type: Object,
		default: null
	},
	/**
	 * API请求头
	 */
	apiHeaders: {
		type: Object,
		default: () => ({})
	},
	/**
	 * API标签字段
	 */
	apiLabel: {
		type: String,
		default: ''
	},
	/**
	 * 响应数据路径
	 */
	resPath: {
		type: String,
		default: ''
	},
	/**
	 * 错误信息路径
	 */
	errorPath: {
		type: String,
		default: ''
	},
	/**
	 * 成功状态码
	 */
	successCode: {
		type: String,
		default: '0'
	},
	/**
	 * 远程异步数据设置函数
	 */
	setDataFn: {
		type: Function,
		default: null
	},
	/**
	 * 前缀文本
	 */
	prefix: {
		type: String,
		default: ''
	},
	/**
	 * 后缀文本
	 */
	suffix: {
		type: String,
		default: ''
	},
	/**
	 * 图标
	 */
	icon: {
		type: String,
		default: ''
	},
	/**
	 * 是否去除空格
	 */
	trim: {
		type: Boolean,
		default: true
	},
	/**
	 * 多选时每次选择都关闭弹窗
	 */
	closeSelection: {
		type: Boolean,
		default: false
	},
	/**
	 * 是否可验证
	 */
	verifiable: {
		type: Boolean,
		default: true
	},
	/**
	 * 是否可比较
	 */
	comparable: {
		type: Boolean,
		default: false
	},
	/**
	 * 自定义标签函数
	 */
	setLabelFn: {
		type: Function,
		default: undefined
	},
	/**
	 * 是否换行
	 */
	wrap: {
		type: Boolean,
		default: false
	},
	/**
	 * 过滤模式下清空数据是否弹出下拉框
	 */
	focusWhenClear: {
		type: Boolean,
		default: false
	},
	/**
	 * 值改变前的回调
	 */
	beforeChange: {
		type: Function,
		default: null
	},
	/**
	 * 值类型
	 */
	valueType: {
		type: String,
		default: ''
	},
	/**
	 * 是否开启远程搜索
	 */
	remote: {
		type: Boolean,
		default: false
	},
	/**
	 * 远程搜索方法
	 */
	remoteMethod: {
		type: Function,
		default: null
	}
}

export type SelectProps = ExtractPropTypes<typeof selectProps>
